import { type FormEvent, useCallback, useEffect, useState } from 'react';
import {
  ApiError,
  getBootstrapStatus,
  getSystemStatus,
  login,
  logout,
  resetPasswordWithMfa,
  restoreSession,
  verifyMfaLogin,
  type MfaChallenge,
  type SessionResponse,
  type SystemStatus,
} from './api';
import { MfaEnrollmentPanel } from './MfaEnrollmentPanel';
import { OperationsDashboard } from './OperationsDashboard';

type AuthState = SessionResponse | null;

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    return error.requestId === undefined
      ? error.message
      : `${error.message}（照会ID: ${error.requestId}）`;
  }
  return '予期しないエラーが発生しました。';
}

export default function App() {
  const [auth, setAuth] = useState<AuthState>(null);
  const [bootstrapRequired, setBootstrapRequired] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordResetMode, setPasswordResetMode] = useState(false);
  const [resetMfaCode, setResetMfaCode] = useState('');
  const [resetNewPassword, setResetNewPassword] = useState('');
  const [resetConfirmPassword, setResetConfirmPassword] = useState('');
  const [mfaChallenge, setMfaChallenge] = useState<MfaChallenge | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [systemStatus, setSystemStatus] = useState<SystemStatus | null>(null);
  const reportError = useCallback((caught: unknown) => setError(errorMessage(caught)), []);

  useEffect(() => {
    let active = true;
    void Promise.allSettled([restoreSession(), getBootstrapStatus()]).then((results) => {
      if (!active) return;
      const [sessionResult, bootstrapResult] = results;
      if (sessionResult.status === 'fulfilled') setAuth(sessionResult.value);
      if (bootstrapResult.status === 'fulfilled')
        setBootstrapRequired(bootstrapResult.value.requiresBootstrap);
      setLoading(false);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (
      auth?.user.globalRole !== 'SUPER_ADMIN' ||
      (auth.mfa.requiredForRole && !auth.mfa.verified)
    ) {
      setSystemStatus(null);
      return;
    }
    let active = true;
    void getSystemStatus()
      .then((status) => {
        if (active) setSystemStatus(status);
      })
      .catch((caught: unknown) => {
        if (active) setError(errorMessage(caught));
      });
    return () => {
      active = false;
    };
  }, [auth]);

  async function handleLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const result = await login(email, password);
      if ('mfaRequired' in result) {
        setMfaChallenge(result);
        setMfaCode('');
      } else {
        setAuth(result);
      }
      setPassword('');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePasswordReset(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    setNotice(null);
    if (resetNewPassword !== resetConfirmPassword) {
      setError('新しいパスワードと確認入力が一致しません。');
      return;
    }
    setSubmitting(true);
    try {
      await resetPasswordWithMfa({
        code: resetMfaCode,
        email: email.trim(),
        newPassword: resetNewPassword,
      });
      setPasswordResetMode(false);
      setResetMfaCode('');
      setResetNewPassword('');
      setResetConfirmPassword('');
      setPassword('');
      setNotice('パスワードを再設定しました。新しいパスワードでログインしてください。');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleMfaLogin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (mfaChallenge === null) return;
    setSubmitting(true);
    setError(null);
    try {
      setAuth(await verifyMfaLogin(mfaChallenge.challengeToken, mfaCode));
      setMfaChallenge(null);
      setMfaCode('');
      setEmail('');
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleLogout() {
    if (auth === null) return;
    setSubmitting(true);
    setError(null);
    try {
      await logout(auth.csrfToken);
      setAuth(null);
      setMfaChallenge(null);
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="app-shell">
      <header className="site-header">
        <div className="brand-mark" aria-hidden="true">
          OC
        </div>
        <div>
          <p className="eyebrow">合同会社 One Color</p>
          <h1>安全訓練教材システム</h1>
        </div>
      </header>
      <main>
        {loading ? (
          <section className="card" aria-live="polite">
            <p>認証状態を確認しています…</p>
          </section>
        ) : auth === null && mfaChallenge !== null ? (
          <section className="auth-layout">
            <div className="intro-panel">
              <p className="eyebrow">SECOND FACTOR</p>
              <h2>管理者本人であることを確認します。</h2>
              <p>認証アプリの6桁コード、または保存した復旧コードを入力してください。</p>
            </div>
            <form className="card login-card" onSubmit={(event) => void handleMfaLogin(event)}>
              <div>
                <p className="eyebrow">MULTI-FACTOR AUTHENTICATION</p>
                <h2>確認コード</h2>
              </div>
              {error !== null && (
                <div className="notice error" role="alert">
                  {error}
                </div>
              )}
              <label>
                6桁コードまたは復旧コード
                <input
                  autoComplete="one-time-code"
                  maxLength={9}
                  onChange={(event) => setMfaCode(event.target.value.toUpperCase())}
                  required
                  value={mfaCode}
                />
              </label>
              <button disabled={submitting} type="submit">
                {submitting ? '確認中…' : '確認してログイン'}
              </button>
              <button
                className="secondary"
                disabled={submitting}
                onClick={() => {
                  setMfaChallenge(null);
                  setMfaCode('');
                  setError(null);
                }}
                type="button"
              >
                メールアドレス入力へ戻る
              </button>
            </form>
          </section>
        ) : auth === null ? (
          <section className="auth-layout">
            <div className="intro-panel">
              <p className="eyebrow">CIVIL ENGINEERING SAFETY</p>
              <h2>現場ごとの安全訓練を、確実な運用へ。</h2>
              <p>教材の生成から確認、配信、実績管理までを一つの流れにまとめる業務システムです。</p>
            </div>
            <form
              className="card login-card"
              onSubmit={(event) =>
                void (passwordResetMode ? handlePasswordReset(event) : handleLogin(event))
              }
            >
              <div>
                <p className="eyebrow">
                  {passwordResetMode ? 'SECURE RECOVERY' : 'SECURE SIGN IN'}
                </p>
                <h2>{passwordResetMode ? 'パスワードの再設定' : 'ログイン'}</h2>
                <p className="muted">
                  {passwordResetMode
                    ? '登録済みの管理者メールと認証アプリの最新コードで本人確認します。'
                    : '管理者から案内されたアカウントを使用してください。'}
                </p>
              </div>
              {bootstrapRequired && (
                <div className="notice warning" role="status">
                  初期管理者が未登録です。運用担当者がセットアップ手順を実行してください。
                </div>
              )}
              {error !== null && (
                <div className="notice error" role="alert">
                  {error}
                </div>
              )}
              {notice !== null && (
                <div className="notice success" role="status">
                  {notice}
                </div>
              )}
              <label>
                メールアドレス
                <input
                  autoComplete="username"
                  inputMode="email"
                  name="email"
                  onChange={(event) => setEmail(event.target.value)}
                  required
                  type="email"
                  value={email}
                />
              </label>
              {passwordResetMode ? (
                <>
                  <label>
                    認証アプリの6桁コード
                    <input
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      maxLength={6}
                      minLength={6}
                      onChange={(event) => setResetMfaCode(event.target.value)}
                      pattern="[0-9]{6}"
                      required
                      value={resetMfaCode}
                    />
                  </label>
                  <label>
                    新しいパスワード
                    <input
                      autoComplete="new-password"
                      maxLength={128}
                      minLength={10}
                      onChange={(event) => setResetNewPassword(event.target.value)}
                      placeholder="10文字以上"
                      required
                      type="password"
                      value={resetNewPassword}
                    />
                  </label>
                  <label>
                    新しいパスワード（確認）
                    <input
                      autoComplete="new-password"
                      maxLength={128}
                      minLength={10}
                      onChange={(event) => setResetConfirmPassword(event.target.value)}
                      required
                      type="password"
                      value={resetConfirmPassword}
                    />
                  </label>
                  <button disabled={submitting || bootstrapRequired} type="submit">
                    {submitting ? '安全に再設定しています…' : 'MFAで確認して再設定'}
                  </button>
                </>
              ) : (
                <>
                  <label>
                    パスワード
                    <input
                      autoComplete="current-password"
                      name="password"
                      onChange={(event) => setPassword(event.target.value)}
                      required
                      type="password"
                      value={password}
                    />
                  </label>
                  <button disabled={submitting || bootstrapRequired} type="submit">
                    {submitting ? '確認中…' : 'ログイン'}
                  </button>
                </>
              )}
              <button
                className="secondary"
                disabled={submitting || bootstrapRequired}
                onClick={() => {
                  setPasswordResetMode((current) => !current);
                  setPassword('');
                  setResetMfaCode('');
                  setResetNewPassword('');
                  setResetConfirmPassword('');
                  setError(null);
                  setNotice(null);
                }}
                type="button"
              >
                {passwordResetMode ? 'ログイン画面へ戻る' : '現在のパスワードを忘れた場合'}
              </button>
            </form>
          </section>
        ) : auth.mfa.requiredForRole && auth.mfa.enrolled && !auth.mfa.verified ? (
          <section className="card mfa-card">
            <p className="eyebrow">ADMIN SECURITY</p>
            <h2>多要素認証で再ログインしてください</h2>
            <p>このセッションは認証アプリによる確認前のため、管理者操作を利用できません。</p>
            <button onClick={() => void handleLogout()} type="button">
              ログアウトして再認証
            </button>
          </section>
        ) : auth.mfa.requiredForRole && !auth.mfa.enrolled ? (
          <MfaEnrollmentPanel
            csrfToken={auth.csrfToken}
            onCompleted={(mfa) => setAuth({ ...auth, mfa })}
          />
        ) : (
          <>
            {error !== null && (
              <div className="notice error global-error" role="alert">
                {error}
              </div>
            )}
            {systemStatus !== null && (
              <span className="sr-only">基盤接続状態: {systemStatus.checks.d1}</span>
            )}
            <OperationsDashboard
              csrfToken={auth.csrfToken}
              onAccountUpdated={setAuth}
              onError={reportError}
              onLogout={() => void handleLogout()}
              user={auth.user}
            />
          </>
        )}
      </main>
      <footer>© One Color LLC · 安全訓練教材システム</footer>
    </div>
  );
}
